import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  loginWithEmail, 
  registerWithEmail, 
  logout as logoutService, 
  getUserProfile, 
  updateUserProfile, 
  resetPassword, 
  loginWithGoogle, 
  loginWithFacebook, 
  loginWithApple, 
  loginWithTwitter,
  UserProfile, 
  RegisterUserData,
  formatAuthError
} from '../../services/firebase/auth.service';
import { User as FirebaseUser } from 'firebase/auth';
import logger from '../../utils/logger';
import { ensureDatabaseOnline } from '../../services/firebase/database-connection';

// Enhanced User interface that combines Firebase User with our database profile
export interface User {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  firstName?: string;
  lastName?: string;
  username?: string;
  emailVerified: boolean;
  lastLogin?: number;
  dateCreated?: number;
  isInstructor?: boolean;
  isAdmin?: boolean; // Admin status from PostgreSQL
}

interface AuthState {
  isAuthenticated: boolean;
  isInitialized: boolean;
  isLoading: boolean;
  user: User | null;
  error: string | null;
}

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  isLoading: false,
  user: null,
  error: null,
};

// Async Thunks for auth operations
export const loginWithEmailAsync = createAsyncThunk<
  User,
  { email: string; password: string },
  { rejectValue: string }
>('auth/loginWithEmail', async ({ email, password }, { rejectWithValue }) => {
  try {
    // email parameter can be either email or username
    const result = await loginWithEmail(email, password);
    
    // Ensure database connection is properly established after auth
    // This is critical for Firebase security rules to work
    await ensureDatabaseOnline();
    
    // Don't fetch profile here - let the auth listener handle it
    // This prevents duplicate fetches and race conditions
    // The auth listener will automatically update the state with profile data
    
    // Return basic user data that will be enriched by the auth listener
    return {
      uid: result.user.uid,
      email: result.user.email,
      displayName: result.user.displayName,
      photoURL: result.user.photoURL || null,
      emailVerified: result.user.emailVerified,
      // These will be filled in by the auth listener
      firstName: undefined,
      lastName: undefined,
      username: undefined,
      lastLogin: undefined,
      dateCreated: undefined
    };
  } catch (error: any) {
    const formattedError = formatAuthError(error);
    return rejectWithValue(formattedError.message);
  }
});

export const registerWithEmailAsync = createAsyncThunk<
  User,
  RegisterUserData,
  { rejectValue: string }
>('auth/registerWithEmail', async (userData, { rejectWithValue }) => {
  try {
    const result = await registerWithEmail(userData);
    
    // Ensure database connection is properly established after auth
    // This is critical for Firebase security rules to work
    await ensureDatabaseOnline();
    
    const profileData = await getUserProfile(result.user.uid);
    
    if (!profileData) {
      return rejectWithValue('User profile not found after registration');
    }
    
    // Return combined user data
    return {
      uid: result.user.uid,
      email: result.user.email,
      displayName: profileData.displayName || result.user.displayName,
      photoURL: profileData.photoURL || result.user.photoURL || '',
      firstName: profileData.firstName,
      lastName: profileData.lastName,
      username: profileData.username,
      emailVerified: result.user.emailVerified, // Using the Firebase user value, not from profile
      lastLogin: profileData.dateModified || profileData.dateCreated,
      dateCreated: profileData.dateCreated
    };
  } catch (error: any) {
    const formattedError = formatAuthError(error);
    return rejectWithValue(formattedError.message);
  }
});

export const logoutAsync = createAsyncThunk<
  void,
  void,
  { rejectValue: string }
>('auth/logoutAsync', async (_, { rejectWithValue, dispatch }) => {
  try {
    logger.info('Logout async action started');
    
    // Force state reset first (this ensures the UI updates even if Firebase auth fails)
    dispatch(logout());
    
    // Clear stored auth state
    try {
      // Auth state is now handled by Firebase natively - no custom storage to clear
      logger.info('Cleared stored auth state');
    } catch (credError) {
      logger.error('Error clearing stored auth state:', credError);
      // Continue with logout even if auth state clearing fails
    }
    
    // Clear service caches and subscriptions to prevent data mixing between users
    try {
      const { clearTrainingCache } = await import('../../services/firebase/training.service');
      clearTrainingCache();
      logger.info('Cleared training cache');

      const { subscriptionManager } = await import('../../services/firebase/subscription-manager');
      subscriptionManager.clearAll();
      logger.info('Cleared all Firebase subscriptions');

      const { statsService } = await import('../../services/firebase/stats.service');
      statsService.cleanupAllListeners();
      logger.info('Cleared stats service listeners');

      const { cleanupOfflineSupport } = await import('../../services/offline/offline-init.service');
      cleanupOfflineSupport();
      logger.info('Cleaned up offline support (profileCache, syncManager)');
    } catch (cacheError: any) {
      logger.error('Error clearing caches:', cacheError);
    }
    
    // Then try to sign out from Firebase (but we don't depend on this succeeding)
    try {
      await logoutService();
      logger.info('Firebase logout successful');
    } catch (firebaseError: any) {
      logger.error('Firebase logout error (continuing anyway):', firebaseError);
      // Don't reject here, we've already updated the state
    }
    
    // Clear any stored authentication state
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem('BYPASS_LOADING', 'false');
    }
    
    logger.info('Logout async action completed');
  } catch (error: any) {
    logger.error('Unexpected error in logout:', error);
    const formattedError = formatAuthError(error);
    return rejectWithValue(formattedError.message);
  }
});

export const resetPasswordAsync = createAsyncThunk<
  void,
  string,
  { rejectValue: string }
>('auth/resetPassword', async (email, { rejectWithValue }) => {
  try {
    await resetPassword(email);
  } catch (error: any) {
    const formattedError = formatAuthError(error);
    return rejectWithValue(formattedError.message);
  }
});

export const updateProfileAsync = createAsyncThunk<
  User,
  { uid: string; profileData: Partial<UserProfile> },
  { rejectValue: string; state: { auth: AuthState } }
>('auth/updateProfile', async ({ uid, profileData }, { rejectWithValue, getState }) => {
  try {
    const updatedProfile = await updateUserProfile(uid, profileData);
    const currentState = getState();

    // Return combined user data
    return {
      uid: updatedProfile.uid,
      email: updatedProfile.email,
      displayName: updatedProfile.displayName || null,
      photoURL: updatedProfile.photoURL || null,
      firstName: updatedProfile.firstName,
      lastName: updatedProfile.lastName,
      username: updatedProfile.username,
      emailVerified: currentState.auth.user?.emailVerified || false, // Keep existing emailVerified state
      lastLogin: updatedProfile.dateModified || updatedProfile.dateCreated,
      dateCreated: updatedProfile.dateCreated
    };
  } catch (error: any) {
    const formattedError = formatAuthError(error);
    return rejectWithValue(formattedError.message);
  }
});

export const loginWithGoogleAsync = createAsyncThunk<
  User | null,
  boolean | undefined,
  { rejectValue: string }
>('auth/loginWithGoogle', async (useRedirect = false, { rejectWithValue }) => {
  try {
    const result = await loginWithGoogle(useRedirect);
    
    // If using redirect, we won't have a result yet
    if (!result || !result.user) {
      return null;
    }
    
    // Ensure database connection is properly established after auth
    // This is critical for Firebase security rules to work
    await ensureDatabaseOnline();
    
    const profileData = await getUserProfile(result.user.uid);
    
    if (!profileData) {
      return rejectWithValue('User profile not found');
    }
    
    // Return combined user data
    return {
      uid: result.user.uid,
      email: result.user.email,
      displayName: profileData.displayName || result.user.displayName,
      photoURL: profileData.photoURL || result.user.photoURL || '',
      firstName: profileData.firstName,
      lastName: profileData.lastName,
      username: profileData.username,
      emailVerified: result.user.emailVerified, // Using the Firebase user value, not from profile
      lastLogin: profileData.dateModified || profileData.dateCreated,
      dateCreated: profileData.dateCreated
    };
  } catch (error: any) {
    const formattedError = formatAuthError(error);
    return rejectWithValue(formattedError.message);
  }
});

export const loginWithFacebookAsync = createAsyncThunk<
  User | null,
  boolean | undefined,
  { rejectValue: string }
>('auth/loginWithFacebook', async (useRedirect = false, { rejectWithValue }) => {
  try {
    const result = await loginWithFacebook(useRedirect);
    
    // If using redirect, we won't have a result yet
    if (!result || !result.user) {
      return null;
    }
    
    // Ensure database connection is properly established after auth
    // This is critical for Firebase security rules to work
    await ensureDatabaseOnline();
    
    const profileData = await getUserProfile(result.user.uid);
    
    if (!profileData) {
      return rejectWithValue('User profile not found');
    }
    
    // Return combined user data
    return {
      uid: result.user.uid,
      email: result.user.email,
      displayName: profileData.displayName || result.user.displayName,
      photoURL: profileData.photoURL || result.user.photoURL || '',
      firstName: profileData.firstName,
      lastName: profileData.lastName,
      username: profileData.username,
      emailVerified: result.user.emailVerified, // Using the Firebase user value, not from profile
      lastLogin: profileData.dateModified || profileData.dateCreated,
      dateCreated: profileData.dateCreated
    };
  } catch (error: any) {
    const formattedError = formatAuthError(error);
    return rejectWithValue(formattedError.message);
  }
});

export const loginWithTwitterAsync = createAsyncThunk<
  User | null,
  boolean | undefined,
  { rejectValue: string }
>('auth/loginWithTwitter', async (useRedirect = false, { rejectWithValue }) => {
  try {
    const result = await loginWithTwitter(useRedirect);
    
    // If using redirect, we won't have a result yet
    if (!result || !result.user) {
      return null;
    }
    
    // Ensure database connection is properly established after auth
    // This is critical for Firebase security rules to work
    await ensureDatabaseOnline();
    
    const profileData = await getUserProfile(result.user.uid);
    
    if (!profileData) {
      return rejectWithValue('User profile not found');
    }
    
    // Return combined user data
    return {
      uid: result.user.uid,
      email: result.user.email,
      displayName: profileData.displayName || result.user.displayName,
      photoURL: profileData.photoURL || result.user.photoURL || '',
      firstName: profileData.firstName,
      lastName: profileData.lastName,
      username: profileData.username,
      emailVerified: result.user.emailVerified, // Using the Firebase user value, not from profile
      lastLogin: profileData.dateModified || profileData.dateCreated,
      dateCreated: profileData.dateCreated
    };
  } catch (error: any) {
    const formattedError = formatAuthError(error);
    return rejectWithValue(formattedError.message);
  }
});

export const loginWithAppleAsync = createAsyncThunk<
  User | null,
  boolean | undefined,
  { rejectValue: string }
>('auth/loginWithApple', async (useRedirect = false, { rejectWithValue }) => {
  try {
    const result = await loginWithApple(useRedirect);
    
    // If using redirect, we won't have a result yet
    if (!result || !result.user) {
      return null;
    }
    
    // Ensure database connection is properly established after auth
    // This is critical for Firebase security rules to work
    await ensureDatabaseOnline();
    
    const profileData = await getUserProfile(result.user.uid);
    
    if (!profileData) {
      return rejectWithValue('User profile not found');
    }
    
    // Return combined user data
    return {
      uid: result.user.uid,
      email: result.user.email,
      displayName: profileData.displayName || result.user.displayName,
      photoURL: profileData.photoURL || result.user.photoURL || '',
      firstName: profileData.firstName,
      lastName: profileData.lastName,
      username: profileData.username,
      emailVerified: result.user.emailVerified, // Using the Firebase user value, not from profile
      lastLogin: profileData.dateModified || profileData.dateCreated,
      dateCreated: profileData.dateCreated
    };
  } catch (error: any) {
    const formattedError = formatAuthError(error);
    return rejectWithValue(formattedError.message);
  }
});

// Function to convert Firebase User and profile data to our User interface
export const convertToUser = (firebaseUser: FirebaseUser, profileData?: UserProfile | null): User => {
  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    displayName: (profileData?.displayName || firebaseUser.displayName) || null,
    photoURL: (profileData?.profilePhotoURL || profileData?.photoURL || firebaseUser.photoURL) || '',
    firstName: profileData?.firstName,
    lastName: profileData?.lastName,
    username: profileData?.username,
    emailVerified: firebaseUser.emailVerified, // From Firebase Auth, not profileData
    lastLogin: profileData?.dateModified || profileData?.dateCreated,
    dateCreated: profileData?.dateCreated,
    isInstructor: profileData?.isInstructor,
    isAdmin: profileData?.isAdmin // Admin status from PostgreSQL API
  };
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Update auth state from Firebase listener
    login: (state, action: PayloadAction<User>) => {
      state.isAuthenticated = true;
      state.user = action.payload;
      state.error = null;
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
    },
    setInitialized: (state) => {
      state.isInitialized = true;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    resetError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Login with email
    builder.addCase(loginWithEmailAsync.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(loginWithEmailAsync.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isAuthenticated = true;
      state.user = action.payload;
    });
    builder.addCase(loginWithEmailAsync.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to login';
      // IMPORTANT: Do NOT clear auth state on login failure - preserve existing session if any
    });
    
    // Register with email
    builder.addCase(registerWithEmailAsync.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(registerWithEmailAsync.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isAuthenticated = true;
      state.user = action.payload;
    });
    builder.addCase(registerWithEmailAsync.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to register';
      // IMPORTANT: Do NOT clear auth state on registration failure - preserve existing session if any
    });
    
    // Logout
    builder.addCase(logoutAsync.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(logoutAsync.fulfilled, (state) => {
      state.isLoading = false;
      state.isAuthenticated = false;
      state.user = null;
    });
    builder.addCase(logoutAsync.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to logout';
    });
    
    // Reset password
    builder.addCase(resetPasswordAsync.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(resetPasswordAsync.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(resetPasswordAsync.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to reset password';
    });
    
    // Update profile
    builder.addCase(updateProfileAsync.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(updateProfileAsync.fulfilled, (state, action) => {
      state.isLoading = false;
      state.user = action.payload;
    });
    builder.addCase(updateProfileAsync.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to update profile';
    });
    
    // Social logins
    // Google
    builder.addCase(loginWithGoogleAsync.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(loginWithGoogleAsync.fulfilled, (state, action) => {
      state.isLoading = false;
      if (action.payload) {
        state.isAuthenticated = true;
        state.user = action.payload;
      }
    });
    builder.addCase(loginWithGoogleAsync.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to login with Google';
    });
    
    // Facebook
    builder.addCase(loginWithFacebookAsync.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(loginWithFacebookAsync.fulfilled, (state, action) => {
      state.isLoading = false;
      if (action.payload) {
        state.isAuthenticated = true;
        state.user = action.payload;
      }
    });
    builder.addCase(loginWithFacebookAsync.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to login with Facebook';
    });
    
    // Twitter
    builder.addCase(loginWithTwitterAsync.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(loginWithTwitterAsync.fulfilled, (state, action) => {
      state.isLoading = false;
      if (action.payload) {
        state.isAuthenticated = true;
        state.user = action.payload;
      }
    });
    builder.addCase(loginWithTwitterAsync.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to login with Twitter';
    });
    
    // Apple
    builder.addCase(loginWithAppleAsync.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(loginWithAppleAsync.fulfilled, (state, action) => {
      state.isLoading = false;
      if (action.payload) {
        state.isAuthenticated = true;
        state.user = action.payload;
      }
    });
    builder.addCase(loginWithAppleAsync.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to login with Apple';
    });
  }
});

export const {
  login,
  logout,
  setInitialized,
  setLoading,
  setError,
  resetError
} = authSlice.actions;

export default authSlice.reducer;